import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Image as ImageIcon, Plus, Edit, Trash2, Save, Loader2, FolderPlus, Grid, Upload } from 'lucide-react';
import FileUploadInput from '../../components/admin/FileUploadInput';
import { getImageUrl } from '../../utils/assetUrlHelper';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PhotosManagementPage() {
  const [activeTab, setActiveTab] = useState('photos');
  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('photo'); // 'photo' or 'category'
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [photoForm, setPhotoForm] = useState({
    image_file: '',
    title: '',
    alt_text: '',
    caption: '',
    category_id: '',
    tags: [],
    order: 0,
    published: true
  });

  // Category form
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    cover_image: '',
    order: 0
  });

  // Tag input
  const [newTag, setNewTag] = useState('');

  // File upload states
  const [fileData, setFileData] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchPhotos();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/media/photo-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/media/photos?published_only=false`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  // Category Management
  const handleCreateCategory = () => {
    setEditingItem(null);
    setCategoryForm({ name: '', cover_image: '', order: 0 });
    setDialogType('category');
    setOpenDialog(true);
  };

  const handleEditCategory = (category) => {
    setEditingItem(category);
    setCategoryForm({
      name: category.name,
      cover_image: category.cover_image,
      order: category.order
    });
    setDialogType('category');
    setOpenDialog(true);
  };

  const uploadFile = async (file, subfolder) => {
    const formData = new FormData();
    formData.append('file', file);
    // Move subfolder to query parameter to match backend expectation
    const res = await axios.post(`${API_URL}/api/files/upload?subfolder=${subfolder}`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${res.data.id}`;
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      setSaving(true);
      let updatedForm = { ...categoryForm };

      if (fileData.category_cover) {
        const url = await uploadFile(fileData.category_cover, 'photos/covers');
        updatedForm.cover_image = url;
      }

      if (!updatedForm.cover_image) {
        toast.error('Cover image is required');
        return;
      }

      if (editingItem) {
        await axios.put(
          `${API_URL}/api/media/photo-categories/${editingItem.id}`,
          updatedForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Category updated');
      } else {
        await axios.post(
          `${API_URL}/api/media/photo-categories`,
          updatedForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Category created');
      }
      setOpenDialog(false);
      setFileData({});
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail :
        (Array.isArray(detail) ? JSON.stringify(detail) : 'Failed to save category');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Delete this category? Photos in this category must be reassigned first.')) return;

    try {
      await axios.delete(
        `${API_URL}/api/media/photo-categories/${categoryId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Photo Management
  const handleCreatePhoto = () => {
    setEditingItem(null);
    setPhotoForm({
      image_file: '',
      title: '',
      alt_text: '',
      caption: '',
      category_id: categories[0]?.id || '',
      tags: [],
      order: 0,
      published: true
    });
    setDialogType('photo');
    setOpenDialog(true);
  };

  const handleEditPhoto = (photo) => {
    setEditingItem(photo);
    setPhotoForm({
      image_file: photo.image_file,
      title: photo.title || '',
      alt_text: photo.alt_text,
      caption: photo.caption || '',
      category_id: photo.category_id,
      tags: photo.tags || [],
      order: photo.order,
      published: photo.published
    });
    setDialogType('photo');
    setOpenDialog(true);
  };

  const handleSavePhoto = async () => {
    if (!photoForm.alt_text.trim() || !photoForm.category_id) {
      toast.error('Alt text and category are required');
      return;
    }

    const rawFiles = fileData.photo_image;
    const filesToUpload = Array.isArray(rawFiles) ? rawFiles : (rawFiles ? [rawFiles] : []);

    if (filesToUpload.length === 0 && !photoForm.image_file) {
      toast.error('Image file is required');
      return;
    }

    try {
      setSaving(true);

      if (filesToUpload.length > 0) {
        for (let i = 0; i < filesToUpload.length; i++) {
          const singleFile = filesToUpload[i];
          const url = await uploadFile(singleFile, 'photos/gallery');
          const singlePhotoForm = {
            ...photoForm,
            image_file: url,
            title: filesToUpload.length > 1 && photoForm.title 
              ? `${photoForm.title} (${i + 1})` 
              : photoForm.title,
            order: (photoForm.order || 0) + i
          };

          if (editingItem && i === 0) {
            await axios.put(`${API_URL}/api/media/photos/${editingItem.id}`, singlePhotoForm, { headers: getAuthHeaders() });
          } else {
            await axios.post(`${API_URL}/api/media/photos`, singlePhotoForm, { headers: getAuthHeaders() });
          }
        }
        toast.success(filesToUpload.length > 1 ? `${filesToUpload.length} photos saved successfully` : 'Photo saved successfully');
      } else if (editingItem) {
        await axios.put(`${API_URL}/api/media/photos/${editingItem.id}`, photoForm, { headers: getAuthHeaders() });
        toast.success('Photo updated');
      }

      setOpenDialog(false);
      setFileData({});
      fetchPhotos();
    } catch (error) {
      console.error('Error saving photo:', error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save photo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm('Delete this photo?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/media/photos/${photoId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const addTag = () => {
    if (newTag.trim() && !photoForm.tags.includes(newTag.trim())) {
      setPhotoForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setPhotoForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };



  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ImageIcon className="w-8 h-8 text-[#0E6D2B]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Photos Management</h1>
              <p className="text-sm text-gray-600">Manage photo categories and gallery</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="photos">Photos</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
          </TabsList>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">{photos.length} photo(s)</p>
              <Button onClick={handleCreatePhoto} className="bg-[#0E6D2B]" disabled={categories.length === 0}>
                <Plus className="w-4 h-4 mr-2" />
                Add Photo
              </Button>
            </div>

            {categories.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">⚠️ Please create a category first before adding photos.</p>
              </div>
            )}

            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map(photo => (
                <Card key={photo.id}>
                  <CardContent className="p-4">
                    <img src={getImageUrl(photo.image_file)} alt={photo.alt_text} className="w-full h-40 object-cover rounded mb-3" />
                    <p className="font-medium text-sm truncate">{photo.title || 'Untitled'}</p>
                    <p className="text-xs text-gray-500 mb-2">{categories.find(c => c.id === photo.category_id)?.name}</p>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEditPhoto(photo)} size="sm" variant="outline" className="flex-1">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button onClick={() => handleDeletePhoto(photo.id)} size="sm" variant="destructive" className="flex-1">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">{categories.length} category(ies)</p>
              <Button onClick={handleCreateCategory} className="bg-[#0E6D2B]">
                <FolderPlus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map(category => (
                <Card key={category.id}>
                  <CardContent className="p-4">
                    <img src={getImageUrl(category.cover_image)} alt={category.name} className="w-full h-32 object-cover rounded mb-3" />
                    <p className="font-medium">{category.name}</p>
                    <p className="text-xs text-gray-500 mb-3">
                      {photos.filter(p => p.category_id === category.id).length} photo(s)
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEditCategory(category)} size="sm" variant="outline" className="flex-1">
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button onClick={() => handleDeleteCategory(category.id)} size="sm" variant="destructive" className="flex-1">
                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Photo Dialog */}
        {dialogType === 'photo' && (
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit Photo' : 'Add Photo'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <div>
                    <Label>Image *</Label>
                    <FileUploadInput
                      field={{ name: 'image_file', type: 'file', fileType: 'image', multiple: true }}
                      value={fileData.photo_image}
                      existingUrl={photoForm.image_file}
                      onChange={(files) => setFileData({ ...fileData, photo_image: files })}
                      onDeleteServerFile={() => { setPhotoForm({ ...photoForm, image_file: '' }); return true; }}
                    />
                  </div>
                </div>

                <div>
                  <Label>Category *</Label>
                  <Select value={photoForm.category_id} onValueChange={(value) => setPhotoForm({ ...photoForm, category_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={photoForm.title}
                      onChange={(e) => setPhotoForm({ ...photoForm, title: e.target.value })}
                      placeholder="Photo title"
                    />
                  </div>
                  <div>
                    <Label>Alt Text *</Label>
                    <Input
                      value={photoForm.alt_text}
                      onChange={(e) => setPhotoForm({ ...photoForm, alt_text: e.target.value })}
                      placeholder="Description for accessibility"
                    />
                  </div>
                </div>

                <div>
                  <Label>Caption</Label>
                  <Textarea
                    value={photoForm.caption}
                    onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })}
                    placeholder="Photo caption"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Tags</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag"
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    />
                    <Button onClick={addTag} size="sm" type="button">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {photoForm.tags.map(tag => (
                      <span key={tag} className="bg-gray-200 px-2 py-1 rounded text-sm flex items-center gap-1">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="text-red-500 hover:text-red-700">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Order</Label>
                    <Input
                      type="number"
                      value={photoForm.order}
                      onChange={(e) => setPhotoForm({ ...photoForm, order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="photo_published"
                      checked={photoForm.published}
                      onChange={(e) => setPhotoForm({ ...photoForm, published: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="photo_published">Published</Label>
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button onClick={() => setOpenDialog(false)} variant="outline">Cancel</Button>
                  <Button onClick={handleSavePhoto} disabled={saving} className="bg-[#0E6D2B]">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Photo
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Category Dialog */}
        {dialogType === 'category' && (
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit Category' : 'Add Category'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Category Name *</Label>
                  <Input
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="e.g., Research Activities"
                  />
                </div>

                <div>
                  <div>
                    <Label>Cover Image *</Label>
                    <FileUploadInput
                      field={{ name: 'cover_image', type: 'file', fileType: 'image' }}
                      value={fileData.category_cover}
                      existingUrl={categoryForm.cover_image}
                      onChange={(files) => setFileData({ ...fileData, category_cover: files })}
                      onDeleteServerFile={() => { setCategoryForm({ ...categoryForm, cover_image: '' }); return true; }}
                    />
                  </div>
                </div>

                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={categoryForm.order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button onClick={() => setOpenDialog(false)} variant="outline">Cancel</Button>
                  <Button onClick={handleSaveCategory} disabled={saving} className="bg-[#0E6D2B]">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Category
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AdminLayout>
  );
}
